const STORAGE_KEY_DETAIL = "tessera.workspace.drawerWidth.detail";
const STORAGE_KEY_SUMMARY = "tessera.workspace.drawerWidth.summary";
const STORAGE_KEY_STRATEGY = "tessera.workspace.drawerWidth.strategy";

export const WORKSPACE_DRAWER_WIDTH_MIN = 550;
/** Soft absolute ceiling when content width is unknown (SSR / first paint). */
export const WORKSPACE_DRAWER_WIDTH_MAX = 2400;

export const WORKSPACE_DRAWER_WIDTH_DEFAULT_DETAIL = 900;
export const WORKSPACE_DRAWER_WIDTH_DEFAULT_SUMMARY = 560;

/**
 * Gap between master/active pine panels (`gap-3`).
 * Width math assumes `contentWidth` is already the pine dock / drawer-host inner width
 * (outer canvas padding is not included again).
 */
export const WORKSPACE_PINE_DOCK_PANEL_GAP_PX = 12;

export type WorkspaceDrawerWidthKind = "detail" | "summary" | "strategy";

export type WorkspaceDrawerWidthOptions = {
  /** When set, overrides the kind’s default minimum. */
  minWidth?: number;
  /** Preferred open width (strategy → active pine card). */
  preferredWidth?: number;
  /** Hard ceiling (e.g. half host width when two sheets sit side-by-side). */
  maxWidthCap?: number;
};

function storageKey(kind: WorkspaceDrawerWidthKind): string {
  if (kind === "strategy") return STORAGE_KEY_STRATEGY;
  return kind === "detail" ? STORAGE_KEY_DETAIL : STORAGE_KEY_SUMMARY;
}

function defaultWidth(
  kind: WorkspaceDrawerWidthKind,
  options?: WorkspaceDrawerWidthOptions
): number {
  if (kind === "strategy" && options?.preferredWidth != null) {
    return options.preferredWidth;
  }
  if (kind === "strategy" && options?.minWidth != null) {
    return options.minWidth;
  }
  return kind === "summary"
    ? WORKSPACE_DRAWER_WIDTH_DEFAULT_SUMMARY
    : WORKSPACE_DRAWER_WIDTH_DEFAULT_DETAIL;
}

/** Max sheet width = full page content (drawer host / canvas) width. */
export function getWorkspaceDrawerMaxWidth(
  contentWidth?: number,
  options?: WorkspaceDrawerWidthOptions
): number {
  const pageWidth =
    contentWidth != null && contentWidth > 0
      ? contentWidth
      : typeof window !== "undefined"
        ? window.innerWidth
        : WORKSPACE_DRAWER_WIDTH_MAX;
  const full = Math.max(0, Math.floor(pageWidth));
  const cap = options?.maxWidthCap;
  if (cap == null || !Number.isFinite(cap) || cap <= 0) return full;
  return Math.max(0, Math.min(full, Math.floor(cap)));
}

/**
 * Width of one pine panel in the dock (master or active card slot).
 * `dockContentWidth` must be the pine dock grid / drawer-host width (already padded).
 * Sheet minimum width uses this single-card slot even when only one panel is visible.
 */
export function getWorkspaceActivePineCardMinWidth(
  dockContentWidth: number,
  _pinePanelCount?: number
): number {
  const safeWidth = Math.max(0, Math.floor(dockContentWidth));
  const layoutColumns = 2;
  return Math.max(
    0,
    Math.floor(
      (safeWidth - WORKSPACE_PINE_DOCK_PANEL_GAP_PX * (layoutColumns - 1)) / layoutColumns
    )
  );
}

export function clampWorkspaceDrawerWidth(
  width: number,
  contentWidth?: number,
  options?: WorkspaceDrawerWidthOptions
): number {
  const maxAllowed = getWorkspaceDrawerMaxWidth(contentWidth, options);
  const designMin = options?.minWidth ?? WORKSPACE_DRAWER_WIDTH_MIN;
  // Prefer the design min, but never force wider than the page content.
  const minAllowed = Math.min(Math.max(designMin, 0), maxAllowed);
  return Math.round(Math.min(maxAllowed, Math.max(minAllowed, width)));
}

export function readWorkspaceDrawerWidth(
  kind: WorkspaceDrawerWidthKind,
  contentWidth?: number,
  options?: WorkspaceDrawerWidthOptions
): number {
  const fallback = clampWorkspaceDrawerWidth(
    defaultWidth(kind, options),
    contentWidth,
    options
  );
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey(kind));
    if (!raw) return fallback;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return fallback;
    return clampWorkspaceDrawerWidth(parsed, contentWidth, options);
  } catch {
    return fallback;
  }
}

export function writeWorkspaceDrawerWidth(
  kind: WorkspaceDrawerWidthKind,
  width: number,
  contentWidth?: number,
  options?: WorkspaceDrawerWidthOptions
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      storageKey(kind),
      String(clampWorkspaceDrawerWidth(width, contentWidth, options))
    );
  } catch {
    // ignore quota / private mode
  }
}

/** Pixels within max that still count as “full width” for toggle. */
export const WORKSPACE_DRAWER_FULL_WIDTH_EPSILON_PX = 4;

/**
 * Double-click resize grip: expand to full content width, or restore the
 * width from before expand. If already full with no stored previous, falls
 * back to `minWidth`.
 */
export function resolveWorkspaceDrawerFullWidthToggle(args: {
  width: number;
  maxWidth: number;
  minWidth: number;
  widthBeforeFull: number | null;
  epsilon?: number;
}): { nextWidth: number; nextWidthBeforeFull: number | null } {
  const epsilon = args.epsilon ?? WORKSPACE_DRAWER_FULL_WIDTH_EPSILON_PX;
  const atFull = args.maxWidth > 0 && args.width >= args.maxWidth - epsilon;
  if (atFull) {
    const restore =
      args.widthBeforeFull != null && Number.isFinite(args.widthBeforeFull)
        ? args.widthBeforeFull
        : args.minWidth;
    return { nextWidth: restore, nextWidthBeforeFull: null };
  }
  return { nextWidth: args.maxWidth, nextWidthBeforeFull: args.width };
}
